import { formatters } from './formatters';
import { evaluateCondition } from './conditionalLogicUtils';
import { FormFieldParser } from '../application-form/FormFieldParser';

/**
 * Formats a value for display based on the question type
 */
function formatValueForComposite(
  value: PolicyFormSubmissionDataValue,
  question: PolicyFormQuestion,
): string {
  if (value === undefined || value === null) return '';

  if (question.type === 'date' && value) {
    const dateStr = String(value);
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      // Return ISO date format (YYYY-MM-DD) for consistency with HTML date inputs
      return date.toISOString().split('T')[0];
    }
    return dateStr;
  }

  if (question.type === 'radio') {
    const selectedOption = question.options?.find(option => option.id === value);
    if (selectedOption) {
      return selectedOption.value;
    }
    return String(value);
  }

  if (question.type === 'multiselect') {
    const stringValue = String(value);
    if (!stringValue) return '';

    const selectedIds = stringValue.split(',').filter(Boolean);
    if (!question.options || selectedIds.length === 0) {
      return stringValue;
    }

    const selectedValues = selectedIds
      .map(id => {
        const option = question.options?.find(opt => opt.id === id);
        return option ? option.value : id;
      })
      .filter(Boolean);

    return selectedValues.join(', ');
  }

  const stringValue = String(value);

  // Apply formatter if validation type exists and formatter is available
  if (question.validationType && formatters[question.validationType]) {
    return formatters[question.validationType](stringValue);
  }

  return stringValue;
}

/**
 * Type for interpolation parameters (optional, client-side only feature)
 */
type InterpolationParameters = Record<string, string | number | boolean> | undefined;

/**
 * Interpolates text with parameters (if provided)
 * This is a simplified version that works both client and server-side
 */
function interpolateTitle(text: string, parameters?: InterpolationParameters): string {
  if (!parameters || !text) return text;

  let result = text;
  Object.entries(parameters).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), String(value));
  });
  return result;
}

/**
 * Calculates the composite value for a specific field based on its configuration
 * @param question - The question with composite configuration
 * @param formData - Current form submission data
 * @param questions - Question registry with all question definitions
 * @param parameters - Optional interpolation parameters for question titles (client-side)
 * @returns The calculated composite value as a string
 */
export function calculateCompositeValue(
  question: PolicyFormQuestion,
  formData: PolicyFormSubmissionData,
  questions: Record<string, PolicyFormQuestion>,
  parameters?: InterpolationParameters,
): string {
  if (!question.composite) {
    return '';
  }

  const { questionRefs, delimiter, includeMetadata, conditional } = question.composite;

  // If conditional is provided, evaluate it first
  if (conditional) {
    const shouldCompose = evaluateCondition(conditional, formData, parameters || {});
    if (!shouldCompose) {
      return '';
    }
  }

  if (!questionRefs || questionRefs.length === 0) {
    return '';
  }

  const compositeValues = questionRefs
    .map(ref => {
      const referencedQuestion = questions[ref];
      const rawValue = formData[ref];

      if (!referencedQuestion || rawValue === undefined || rawValue === null || rawValue === '') {
        return '';
      }

      const formattedValue = formatValueForComposite(rawValue, referencedQuestion);

      if (!formattedValue) {
        return '';
      }

      if (includeMetadata) {
        const questionTitle = interpolateTitle(referencedQuestion.title?.trim() || '', parameters);
        const separator = questionTitle.endsWith(':') ? ' ' : ': ';
        return `${questionTitle}${separator}${formattedValue}`;
      }

      return formattedValue;
    })
    .filter(Boolean);

  return compositeValues.join(delimiter || ', ');
}

function isChoiceInput(question: PolicyFormQuestion) {
  return question.type === 'radio' || question.type === 'select';
}

function isEmptyValue(value: unknown) {
  return (
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
  );
}

function applyParserIfNeeded(rawValue: string | undefined | null, question: PolicyFormQuestion) {
  if (isEmptyValue(rawValue)) return undefined;
  const stringValue = String(rawValue);
  return FormFieldParser.parse(question, stringValue);
}

export function calculateCompositeFieldValues(
  formData: PolicyFormSubmissionData,
  questions: Record<string, PolicyFormQuestion>,
  changedQuestionRef?: string,
): PolicyFormSubmissionData {
  const updatedFormData: PolicyFormSubmissionData = { ...formData };

  function hasChangedSource(sourceRefs?: string[]) {
    return Boolean(changedQuestionRef && sourceRefs?.includes(changedQuestionRef));
  }

  function updateOrRemoveValue(fieldRef: string, valueToSet?: string) {
    if (isEmptyValue(valueToSet)) {
      delete updatedFormData[fieldRef];
    } else {
      updatedFormData[fieldRef] = valueToSet!;
    }
  }

  function computeCompositeValue(question: PolicyFormQuestion) {
    return calculateCompositeValue(question, formData, questions);
  }

  for (const [questionRef, questionDefinition] of Object.entries(questions)) {
    const compositeConfig = questionDefinition.composite;

    if (!compositeConfig) continue;

    // Conditional composites — skip if condition not met
    const conditionMet =
      !compositeConfig.conditional || evaluateCondition(compositeConfig.conditional, formData, {});
    if (!conditionMet) continue;

    // compositeFieldRef — write computed value to another field
    if (compositeConfig.compositeFieldRef) {
      const computedValue = applyParserIfNeeded(
        computeCompositeValue(questionDefinition),
        questionDefinition,
      );

      updateOrRemoveValue(compositeConfig.compositeFieldRef, computedValue);

      if (!(questionRef in updatedFormData)) {
        updatedFormData[questionRef] = '';
      }
      continue;
    }

    // AllowAppendText (editable text fields)
    if (compositeConfig.allowAppendText && !isChoiceInput(questionDefinition)) {
      const sourceFieldChanged = hasChangedSource(compositeConfig.questionRefs);

      if (sourceFieldChanged) {
        const computedValue = applyParserIfNeeded(
          computeCompositeValue(questionDefinition),
          questionDefinition,
        );
        updateOrRemoveValue(questionRef, computedValue);
      }

      continue;
    }

    // Choice inputs (radio/select)
    if (isChoiceInput(questionDefinition)) {
      if (compositeConfig.allowAppendText && !hasChangedSource(compositeConfig.questionRefs)) {
        continue;
      }

      // Check all questionRefs and use the first non-empty value
      let mappedValue: string | undefined;
      for (const sourceRef of compositeConfig.questionRefs || []) {
        const rawSourceValue = formData[sourceRef];

        if (!isEmptyValue(rawSourceValue)) {
          const rawValueString = String(rawSourceValue);
          mappedValue = compositeConfig.mapping?.[rawValueString] ?? rawValueString;
          break; // Use the first non-empty value found
        }
      }

      if (mappedValue !== undefined) {
        updatedFormData[questionRef] = mappedValue;
      }
      continue;
    }

    // Default case — recompute and update or delete value
    const computedValue = applyParserIfNeeded(
      computeCompositeValue(questionDefinition),
      questionDefinition,
    );
    updateOrRemoveValue(questionRef, computedValue);
  }

  return updatedFormData;
}

export function applyCompositeFields(
  formData: PolicyFormSubmissionData,
  questions: Record<string, PolicyFormQuestion>,
): PolicyFormSubmissionData {
  const updatedFormData: PolicyFormSubmissionData = { ...formData };

  const computeCompositeValue = (question: PolicyFormQuestion) =>
    calculateCompositeValue(question, updatedFormData, questions);

  for (const [questionRef, questionDefinition] of Object.entries(questions)) {
    const composite = questionDefinition.composite;
    if (!composite) continue;

    // Check conditional logic; skip if not met
    const conditionMet =
      !composite.conditional || evaluateCondition(composite.conditional, updatedFormData, {});
    if (!conditionMet) continue;

    // Determine the value to apply
    let valueToSet: string | undefined;
    let sourceValue: string | undefined;

    if (isChoiceInput(questionDefinition) && composite.questionRefs?.length) {
      // Check all questionRefs and use the first non-empty value
      for (const sourceRef of composite.questionRefs) {
        const rawSource = updatedFormData[sourceRef];

        if (!isEmptyValue(rawSource)) {
          const rawString = String(rawSource);
          const mapped = composite.mapping?.[rawString] ?? rawString;
          valueToSet = mapped;
          sourceValue = mapped;
          break; // Use the first non-empty value found
        }
      }
    } else {
      valueToSet = applyParserIfNeeded(
        computeCompositeValue(questionDefinition),
        questionDefinition,
      );
      sourceValue = valueToSet;
    }

    // Apply only if we have a non-empty value
    if (!isEmptyValue(valueToSet)) {
      const targetRef = composite.compositeFieldRef ?? questionRef;
      const currentValue = updatedFormData[targetRef];

      // Only update if the composite field is empty or equals its source field value
      if (isEmptyValue(currentValue) || currentValue === sourceValue) {
        updatedFormData[targetRef] = valueToSet!;
      }
    }
  }

  return updatedFormData;
}
